import type { JSX, MouseEvent, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@feathr/components';

import * as styles from './ImportDetailsButton.css';

/** Opens and closes an imports-table row's Import details panel. */
export interface IImportDetailsToggle {
  isExpanded: boolean;
  onToggle: () => void;
}

interface IImportDetailsButtonProps extends IImportDetailsToggle {
  children: ReactNode;
  /** The chip's text, such as "Complete". The button's name adds what clicking it does. */
  label: string;
}

/** Makes a table chip open and close its row's Import details, as the row's arrow does. */
function ImportDetailsButton({
  children,
  isExpanded,
  label,
  onToggle,
}: Readonly<IImportDetailsButtonProps>): JSX.Element {
  const { t } = useTranslation();

  // Stop the click before it bubbles to the row.
  function handleClick(event: MouseEvent<Element>): void {
    event.stopPropagation();
    onToggle();
  }

  return (
    <Button
      aria-expanded={isExpanded}
      aria-label={t('{{status}}, view import details', { status: label })}
      className={styles.root}
      onClick={handleClick}
      tooltip={t('Click to view details')}
      type={'naked'}
    >
      {children}
    </Button>
  );
}

export default ImportDetailsButton;
